<script setup lang="ts">
import type { PlanName } from '~/composables/usePlanConfig'
import type { PricingTableSection, PricingTableTier } from '@nuxt/ui'
import { PRICES_EUR } from '~/composables/useCurrency'

const { data: page } = await useAsyncData('pricing', () => queryCollection('pricing').first())

const title = page.value?.seo?.title || page.value?.title
const description = page.value?.seo?.description || page.value?.description

useSeoMeta({
  title,
  ogTitle: title,
  description,
  ogDescription: description
})

defineOgImageComponent('Saas')

// JSON-LD: Product + Offer per paid plan (EUR, matching the displayed payment
// mode) + FAQPage from the pricing content. Each Product/Offer needs a distinct
// @id or nuxt-schema-org collapses them into one node (auto @id is per-page).
const schemaSiteUrl = useRuntimeConfig().public.siteUrl
const schemaPaymentMode = (useRuntimeConfig().public.paymentMode as 'onetime' | 'subscription') || 'onetime'
const schemaTier = schemaPaymentMode === 'onetime' ? 'lifetime' : 'monthly'
useSchemaOrg([
  defineProduct({
    '@id': `${schemaSiteUrl}/pricing#product-pro`,
    'name': 'mychar.gg Pro',
    'description': 'Full creative control for your link-in-bio page: background effects and cursor trails, video backgrounds and clips, custom links, YouTube and Twitch integrations, custom fonts, advanced analytics, and no mychar.gg branding.',
    'offers': [defineOffer({
      '@id': `${schemaSiteUrl}/pricing#offer-pro`,
      'price': PRICES_EUR.pro[schemaTier],
      'priceCurrency': 'EUR',
      'availability': 'https://schema.org/InStock',
      'url': `${schemaSiteUrl}/pricing`
    })]
  }),
  defineProduct({
    '@id': `${schemaSiteUrl}/pricing#product-elite`,
    'name': 'mychar.gg Elite',
    'description': 'Everything in Pro plus exclusive effects and cursor trails, exclusive icon styles, custom font uploads, unlimited custom links and friends, link embeds, lifetime analytics, the highest limits, and priority support.',
    'offers': [defineOffer({
      '@id': `${schemaSiteUrl}/pricing#offer-elite`,
      'price': PRICES_EUR.elite[schemaTier],
      'priceCurrency': 'EUR',
      'availability': 'https://schema.org/InStock',
      'url': `${schemaSiteUrl}/pricing`
    })]
  }),
  defineWebPage({ '@type': ['CollectionPage', 'FAQPage'] }),
  ...(page.value?.faq?.items ?? []).map(item =>
    defineQuestion({ name: item.label, acceptedAnswer: item.content })
  )
])

const authStore = useAuthStore()
const toast = useToast()
const { trackGoal } = useDatafast()
const { formatPrice, getUpgradePrice, currency, currencies, setCurrency } = useCurrency()

const isProToEliteUpgrade = computed(() =>
  isOnetime.value && authStore.isLifetime && authStore.plan === 'pro'
)
const runtimeConfig = useRuntimeConfig()
const paymentMode = computed(() => (runtimeConfig.public.paymentMode as 'onetime' | 'subscription') || 'onetime')
const isOnetime = computed(() => paymentMode.value === 'onetime')

const billingCycle = ref<'monthly' | 'yearly'>('monthly')
const checkoutLoading = ref<string | null>(null)

const PLAN_RANK: Record<PlanName, number> = { free: 0, pro: 1, elite: 2 }

const plansForMode = computed(() => planCardsForMode(isOnetime.value))

function isPlanHighlighted(plan: PlanName): boolean {
  if (authStore.plan === 'free') return plan === 'pro'
  if (authStore.plan === 'pro') return plan === 'elite'
  return false
}

function getButtonLabel(plan: PlanName): string {
  if (!authStore.userId) {
    if (plan === 'free') return 'Get Started'
    if (isOnetime.value) return plan === 'pro' ? 'Get Pro' : 'Get Elite'
    if (plan === 'pro') return 'Start 7-Day Free Trial'
    return 'Get Elite'
  }
  if (plan === authStore.plan) return 'Current Plan'
  if (isOnetime.value && authStore.isLifetime && PLAN_RANK[plan] < PLAN_RANK[authStore.plan]) return 'Already Higher'
  if (PLAN_RANK[plan] < PLAN_RANK[authStore.plan]) return 'Downgrade'
  if (isOnetime.value && authStore.isLifetime && authStore.plan === 'pro' && plan === 'elite') return 'Upgrade to Elite'
  if (isOnetime.value) return 'Get Lifetime'
  if (plan === 'pro' && !authStore.hasUsedTrial) return 'Start 7-Day Free Trial'
  return 'Upgrade'
}

async function handlePlanAction(plan: PlanName) {
  // Not logged in - send to signup
  if (!authStore.userId) {
    await navigateTo('/signup')
    return
  }

  // Already on this plan or lifetime user trying to downgrade - no action
  if (plan === authStore.plan) return
  if (authStore.isLifetime && PLAN_RANK[plan] < PLAN_RANK[authStore.plan]) return

  // Active subscriber - use Stripe Portal for any plan change (shows price, user confirms).
  // Lifetime users skip the portal — they have no recurring sub and upgrade via a new lifetime checkout.
  const isActiveSub = authStore.subscription?.isActive && authStore.plan !== 'free' && !authStore.isLifetime
  if (isActiveSub) {
    checkoutLoading.value = plan
    try {
      const data = await $fetch<{ url: string }>('/api/stripe/portal', { method: 'POST' })
      if (data.url) {
        await navigateTo(data.url, { external: true })
      }
    } catch (err: any) {
      toast.add({
        title: 'Error',
        description: err.data?.message || 'Failed to open subscription portal.',
        icon: 'i-lucide-x',
        color: 'error'
      })
    } finally {
      checkoutLoading.value = null
    }
    return
  }

  // No subscription or canceled - new checkout session
  checkoutLoading.value = plan
  trackGoal('checkout_started', {
    type: 'plan',
    plan,
    billing_cycle: isOnetime.value ? 'lifetime' : billingCycle.value,
    context: 'pricing'
  })
  try {
    const data = await $fetch<{ url: string }>('/api/stripe/checkout', {
      method: 'POST',
      body: isOnetime.value
        ? { plan }
        : { plan, billingCycle: billingCycle.value }
    })
    if (data.url) {
      await navigateTo(data.url, { external: true })
    }
  } catch (err: any) {
    toast.add({
      title: 'Error',
      description: err.data?.message || 'Failed to start checkout. Please try again.',
      icon: 'i-lucide-x',
      color: 'error'
    })
  } finally {
    checkoutLoading.value = null
  }
}

// ---------------------------------------------------------------------------
// Feature comparison table
// ---------------------------------------------------------------------------

const comparisonTiers = computed<PricingTableTier[]>(() => [
  {
    id: 'free',
    title: 'Free',
    description: 'Start for free, forever.',
    price: formatPrice('free', isOnetime.value ? 'lifetime' : billingCycle.value),
    billingCycle: '/forever',
    badge: undefined,
    highlight: false,
    button: {
      label: getButtonLabel('free'),
      color: 'neutral' as any,
      variant: 'outline' as any,
      disabled: authStore.userId ? ('free' === authStore.plan || (authStore.isLifetime && PLAN_RANK['free'] < PLAN_RANK[authStore.plan])) : false,
      onClick: () => handlePlanAction('free')
    }
  },
  {
    id: 'pro',
    title: 'Pro',
    description: 'Full creative control.',
    price: formatPrice('pro', isOnetime.value ? 'lifetime' : billingCycle.value),
    billingCycle: isOnetime.value ? ' · one-time' : (billingCycle.value === 'monthly' ? '/month' : '/year'),
    badge: authStore.userId && authStore.plan === 'pro' ? 'Current Plan' : (isPlanHighlighted('pro') ? 'Most Popular' : undefined),
    highlight: isPlanHighlighted('pro'),
    button: {
      label: getButtonLabel('pro'),
      color: (authStore.userId && authStore.plan === 'pro') ? 'neutral' as any : (isPlanHighlighted('pro') ? 'primary' as any : 'neutral' as any),
      variant: (authStore.userId && authStore.plan === 'pro') ? 'ghost' as any : (isPlanHighlighted('pro') ? 'solid' as any : 'outline' as any),
      disabled: authStore.userId ? (authStore.plan === 'pro' || (authStore.isLifetime && PLAN_RANK['pro'] < PLAN_RANK[authStore.plan])) : false,
      loading: checkoutLoading.value === 'pro',
      onClick: () => handlePlanAction('pro')
    }
  },
  {
    id: 'elite',
    title: 'Elite',
    description: 'Maximum everything.',
    price: isProToEliteUpgrade.value ? getUpgradePrice('pro', 'elite') : formatPrice('elite', isOnetime.value ? 'lifetime' : billingCycle.value),
    billingCycle: isOnetime.value ? (isProToEliteUpgrade.value ? ' · upgrade price' : ' · one-time') : (billingCycle.value === 'monthly' ? '/month' : '/year'),
    badge: authStore.userId && authStore.plan === 'elite' ? 'Current Plan' : (isPlanHighlighted('elite') ? 'Recommended' : undefined),
    highlight: isPlanHighlighted('elite'),
    button: {
      label: getButtonLabel('elite'),
      color: (authStore.userId && authStore.plan === 'elite') ? 'neutral' as any : (isPlanHighlighted('elite') ? 'primary' as any : 'neutral' as any),
      variant: (authStore.userId && authStore.plan === 'elite') ? 'ghost' as any : (isPlanHighlighted('elite') ? 'solid' as any : 'outline' as any),
      disabled: authStore.userId && authStore.plan === 'elite',
      loading: checkoutLoading.value === 'elite',
      onClick: () => handlePlanAction('elite')
    }
  }
])

// ---------------------------------------------------------------------------
// Add-on spotlight (comparison table rows can't be pressed, so the table
// shows 'Add-on' and this scrolls + pulses the card)
// ---------------------------------------------------------------------------

const addonPulse = ref(false)

function scrollToAddon() {
  document.getElementById('domain-addon-card')?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  addonPulse.value = true
  setTimeout(() => {
    addonPulse.value = false
  }, 2400)
}

// Counts exclude the 'none' placeholder option; icon styles count 'default' as a real style.
const comparisonSections: PricingTableSection[] = [
  {
    title: 'Profile',
    features: [
      { title: 'Avatar uploads', tiers: { free: '2', pro: '10', elite: '25' } },
      { title: 'Avatar shapes', tiers: { free: '1', pro: '2', elite: '3' } },
      { title: 'Avatar borders', tiers: { free: false, pro: true, elite: true } },
      { title: 'Display name', tiers: { free: false, pro: true, elite: true } },
      { title: 'Rich text bio', tiers: { free: false, pro: true, elite: true } },
      { title: 'Fonts', tiers: { free: '3', pro: 'All 8', elite: 'All 8' } },
      { title: 'Custom font uploads', tiers: { free: false, pro: false, elite: 'Up to 5' } },
      { title: 'Pronouns & location', tiers: { free: true, pro: true, elite: true } }
    ]
  },
  {
    title: 'Customization',
    features: [
      { title: 'Page layouts', tiers: { free: '2', pro: '3', elite: '4' } },
      { title: 'Background effects', tiers: { free: false, pro: '7', elite: '13' } },
      { title: 'Background gradient', tiers: { free: false, pro: true, elite: true } },
      { title: 'Video backgrounds', tiers: { free: false, pro: true, elite: true } },
      { title: 'Cursor trails', tiers: { free: false, pro: '3', elite: '10' } },
      { title: 'Custom cursor uploads', tiers: { free: false, pro: '3', elite: '10' } },
      { title: 'Icon styles', tiers: { free: '3', pro: '6', elite: '12' } },
      { title: 'Link styles', tiers: { free: '2', pro: '5', elite: '5' } },
      { title: 'Card animations', tiers: { free: false, pro: '4', elite: '5' } },
      { title: 'Card hover effects', tiers: { free: false, pro: '3', elite: '4' } }
    ]
  },
  {
    title: 'Media & Content',
    features: [
      { title: 'Background uploads', tiers: { free: '1', pro: '5', elite: '15' } },
      { title: 'Video clips', tiers: { free: false, pro: '6', elite: '18' } },
      { title: 'Audio tracks', tiers: { free: '1', pro: '3', elite: '6' } },
      { title: 'Unlimited platform links', tiers: { free: true, pro: true, elite: true } },
      { title: 'Custom links', tiers: { free: false, pro: '3', elite: 'Unlimited' } },
      { title: 'Friends', tiers: { free: '10', pro: '25', elite: 'Unlimited' } },
      { title: 'Game library & achievements', tiers: { free: true, pro: true, elite: true } },
      { title: 'Setup section (gear, specs & photo)', tiers: { free: true, pro: true, elite: true } },
      { title: 'Section reordering', tiers: { free: true, pro: true, elite: true } }
    ]
  },
  {
    title: 'Connections',
    features: [
      { title: 'Discord profile', tiers: { free: true, pro: true, elite: true } },
      { title: 'YouTube channel & videos', tiers: { free: false, pro: true, elite: true } },
      { title: 'Twitch channel, clips & schedule', tiers: { free: false, pro: true, elite: true } },
      { title: 'Show links as embed', tiers: { free: false, pro: false, elite: true } }
    ]
  },
  {
    title: 'Analytics',
    features: [
      { title: 'History', tiers: { free: '7 days', pro: '30 days', elite: 'Lifetime' } },
      { title: 'Country breakdown', tiers: { free: false, pro: true, elite: true } },
      { title: 'Live visitors', tiers: { free: false, pro: true, elite: true } }
    ]
  },
  {
    title: 'Extras',
    features: [
      { title: 'Custom domain', tiers: { free: 'Add-on', pro: 'Add-on', elite: 'Add-on' } },
      { title: 'Comment section', tiers: { free: false, pro: true, elite: true } },
      { title: 'Custom enter label', tiers: { free: false, pro: true, elite: true } },
      { title: 'Custom section titles', tiers: { free: false, pro: true, elite: true } },
      { title: 'No mychar.gg branding', tiers: { free: false, pro: true, elite: true } },
      { title: 'Priority support', tiers: { free: false, pro: false, elite: true } }
    ]
  }
]
</script>

<template>
  <div v-if="page">
    <UPageHero
      :title="page.title"
      :description="page.description"
      :ui="{ container: 'pb-8 sm:pb-10 lg:pb-12' }"
    />

    <UContainer>
      <!-- Billing cycle toggle + currency switcher -->
      <div class="flex flex-col items-center gap-3 mb-12">
        <div
          v-if="!isOnetime"
          class="inline-flex items-center rounded-lg border border-accented p-0.5"
        >
          <button
            class="px-4 py-1.5 rounded-md text-sm font-medium transition-colors duration-200"
            :class="billingCycle === 'monthly'
              ? 'bg-elevated text-highlighted'
              : 'text-muted hover:text-toned'"
            @click="billingCycle = 'monthly'"
          >
            Monthly
          </button>
          <button
            class="px-4 py-1.5 rounded-md text-sm font-medium transition-colors duration-200 flex items-center gap-2"
            :class="billingCycle === 'yearly'
              ? 'bg-elevated text-highlighted'
              : 'text-muted hover:text-toned'"
            @click="billingCycle = 'yearly'"
          >
            Yearly
            <span class="text-xs text-success font-semibold">Save up to 25%</span>
          </button>
        </div>
        <div
          v-else
          class="text-sm text-muted"
        >
          <UIcon
            name="i-lucide-infinity"
            class="size-4 inline-block mr-1.5 align-text-bottom text-success-400"
          />
          Pay once · Lifetime access
        </div>
        <div class="inline-flex items-center rounded-lg border border-default p-0.5">
          <button
            v-for="c in currencies"
            :key="c"
            class="px-2.5 py-1 rounded-md text-xs font-medium transition-colors duration-200"
            :class="currency === c
              ? 'bg-elevated text-highlighted'
              : 'text-dimmed hover:text-muted'"
            @click="setCurrency(c)"
          >
            {{ c }}
          </button>
        </div>
      </div>

      <!-- Plan cards -->
      <PlanCards
        :plans="plansForMode"
        :billing-cycle="billingCycle"
        :checkout-loading="checkoutLoading"
        :get-button-label="getButtonLabel"
        :payment-mode="paymentMode"
        class="max-w-5xl mx-auto"
        @plan-action="handlePlanAction"
      />

      <p class="mt-6 flex items-center justify-center gap-1.5 text-sm text-muted">
        <UIcon
          name="i-lucide-lock"
          class="size-3.5"
        />
        Payments handled by Stripe. Cancel anytime from your dashboard.
      </p>

      <!-- Add-ons -->
      <div
        id="addons"
        class="max-w-5xl mx-auto mt-20"
      >
        <div class="text-center mb-8">
          <h2 class="text-2xl font-bold text-highlighted mb-2">
            Add-ons
          </h2>
          <p class="text-sm text-muted">
            Standalone extras billed monthly. They work with every plan, including Free.
          </p>
        </div>
        <div
          id="domain-addon-card"
          class="max-w-md mx-auto rounded-2xl transition-shadow duration-500"
          :class="addonPulse ? 'ring-2 ring-teal-400/70' : ''"
        >
          <AddonCard
            addon="custom_domain"
            context="pricing"
          />
        </div>
      </div>
    </UContainer>

    <!-- Feature comparison table -->
    <UPageSection
      id="compare"
      title="Compare all features"
      description="Exactly what each plan includes."
    >
      <div class="overflow-x-auto">
        <UPricingTable
          :tiers="comparisonTiers"
          :sections="comparisonSections"
          class="max-w-5xl mx-auto"
        />
      </div>

      <!-- Custom domain lives outside the plans -->
      <div class="flex justify-center mt-6">
        <button
          class="group flex items-center gap-2.5 px-4 py-2.5 rounded-xl border border-teal-400/20 bg-teal-500/5 hover:bg-teal-500/10 hover:border-teal-400/40 transition-colors duration-200 cursor-pointer"
          @click="scrollToAddon"
        >
          <UIcon
            name="i-lucide-globe"
            class="size-4 text-teal-400"
          />
          <span class="text-sm text-toned group-hover:text-highlighted transition-colors">
            Custom domain is a standalone add-on for every plan
          </span>
          <UIcon
            name="i-lucide-arrow-up"
            class="size-4 text-teal-400/70 group-hover:text-teal-400 transition-colors"
          />
        </button>
      </div>
    </UPageSection>

    <UPageSection
      :title="page.faq.title"
      :description="page.faq.description"
    >
      <UAccordion
        :items="page.faq.items"
        :unmount-on-hide="false"
        type="multiple"
        class="max-w-3xl mx-auto flex flex-col gap-3"
        :ui="{
          item: 'rounded-xl border border-default bg-elevated/40 px-4 border-b-0 last:border-b-0 shadow-(--surface-shadow)',
          trigger: 'text-base text-highlighted',
          body: 'text-base text-muted'
        }"
      />
    </UPageSection>

    <!-- Bottom CTA -->
    <CtaPanel />
  </div>
</template>
